import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';
import { MultiSelectField } from '@ui/components/ui/multi-select';
import { Button } from '@ui/components/ui/button';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';
import { Skeleton } from '@ui/components/ui/skeleton';
import {
  IError,
  MembershipEntity,
  SendSprintFormValues,
  SprintProductEntity,
  useClientCompany,
  useListClientCompanies,
  useProduct,
  useProductManagerRecipients,
  usePreviousRecipients,
  useListSprintProductClients,
  useSprintReportMailDraft,
} from '@data';
import cx from 'classnames';
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseMutateFunction } from '@tanstack/react-query';
import { useOrganizationId } from '@app/utils/hooks';
import { useStore } from '@data/storages';
import {
  ApprovalRecipientsField,
  type ApprovalRecipientsFieldHandle,
  type FormOptionType,
} from '@app/components/core/approvalPopup/ApprovalRecipientsField';
import { MailPreview } from '@app/components/shared/mailPreview';
import type { MailBlock } from '@data/utils/types';
import AddRecipientsToClientDialog, { recipientsFrom, type Recipient } from './AddRecipientsToClientDialog';
import { readMailBlocks } from './readMailBlocks';
import SprintPricingPanel from './SprintPricingPanel';
import { useSprintReportCost } from './useSprintReportCost';

export interface SendSprintProps extends React.HTMLAttributes<HTMLDivElement> {
  handleClick: (close: boolean) => void;
  sendSprint: UseMutateFunction<SprintProductEntity, IError, SendSprintFormValues, unknown>;
  sprintProduct?: SprintProductEntity;
  sprintProductClients?: MembershipEntity[];
}

type ColleagueOption = { email: string; name: string; firstName?: string };

// Recipients from the previous send are remembered per product (mock, localStorage) and prefilled —
// a product's reports usually go to the same people sprint after sprint.
const lastSentKey = (productId?: string): string => `mock:lastSentRecipients:${productId ?? 'unknown'}`;
const loadLastSent = (productId?: string): string[] => {
  if (!productId) return [];
  try {
    const parsed = JSON.parse(localStorage.getItem(lastSentKey(productId)) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
};
const saveLastSent = (productId: string | undefined, emails: string[]): void => {
  if (!productId) return;
  try {
    localStorage.setItem(lastSentKey(productId), JSON.stringify(emails));
  } catch {
    /* ignore quota/availability errors — persistence is best-effort */
  }
};

/** How long the pricing panel takes to leave: the 300ms it arrives in, and `duration-300` on it. */
const PRICING_LEAVE_MS = 300;

const toOptions = (emails: string[]): FormOptionType[] =>
  Array.from(new Set(emails.filter(Boolean))).map(email => ({ value: email, label: email, isFixed: false }));

const SendSprintPopup = ({
  handleClick,
  sendSprint,
  sprintProduct,
  sprintProductClients,
}: SendSprintProps): ReactElement => {
  const { t } = useTranslation();
  const productId = sprintProduct?.product?.id;
  const organizationId = useOrganizationId() as string;

  const { data: product } = useProduct(productId);
  const recipientsFieldRef = useRef<ApprovalRecipientsFieldHandle>(null);

  // Offered in the dropdown: the clients who can read this product, not everyone it was ever sent to.
  const colleagueOptions: ColleagueOption[] = useMemo(() => {
    const seen = new Set<string>();
    const options: ColleagueOption[] = [];
    product?.clientAccess?.forEach(client => {
      const email = client.email?.trim();
      if (!email || seen.has(email.toLowerCase())) return;
      seen.add(email.toLowerCase());
      const name = [client.firstName, client.lastName].filter(Boolean).join(' ').trim();
      options.push({ email, name, firstName: client.firstName || undefined });
    });
    return options;
  }, [product?.clientAccess]);

  const colleagueEmails = useMemo(() => new Set(colleagueOptions.map(o => o.email.toLowerCase())), [colleagueOptions]);

  // The product's managers are offered too, and copied in rather than written to, as on the incident mail.
  const { data: managerData } = useProductManagerRecipients(organizationId, productId ? [productId] : []);
  const { tokenData } = useStore();
  const managerOptions: ColleagueOption[] = useMemo(
    () =>
      (managerData?.items ?? [])
        .filter(manager => !colleagueEmails.has(manager.email.toLowerCase()))
        // Whoever sends is offered once, as themselves, below.
        .filter(manager => manager.email.toLowerCase() !== tokenData?.email?.toLowerCase())
        .map(manager => ({ email: manager.email, name: manager.name, firstName: manager.firstName })),
    [managerData, colleagueEmails, tokenData?.email],
  );
  const meOption: ColleagueOption | undefined = tokenData?.email
    ? {
        email: tokenData.email,
        name: [tokenData.firstName, tokenData.lastName].filter(Boolean).join(' ').trim(),
      }
    : undefined;
  const offeredOptions = useMemo(
    () => [...colleagueOptions, ...managerOptions, ...(meOption ? [meOption] : [])],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [colleagueOptions, managerOptions, meOption?.email, meOption?.name],
  );
  const offeredEmails = useMemo(() => new Set(offeredOptions.map(o => o.email.toLowerCase())), [offeredOptions]);
  const managerEmails = useMemo(() => new Set(managerOptions.map(o => o.email.toLowerCase())), [managerOptions]);
  const me = tokenData?.email?.toLowerCase();
  const isMe = (email: string): boolean => !!me && email.toLowerCase() === me;
  const isCopied = (email: string): boolean => managerEmails.has(email.toLowerCase()) || isMe(email);

  // The sender starts copied in and can take themselves off; the rest is seeded from the last send.
  const [recipients, setRecipients] = useState<FormOptionType[]>(() =>
    toOptions([...(tokenData?.email ? [tokenData.email] : []), ...loadLastSent(productId)]),
  );

  // Added to what this browser remembers once both load: this report's recipients, else the previous report's.
  const { data: addressedData, isFetched: addressedFetched } = useListSprintProductClients(sprintProduct?.id);
  const { data: previousData, isFetched: previousFetched } = usePreviousRecipients(sprintProduct?.id);
  const seededFromServer = useRef(false);
  useEffect(() => {
    if (seededFromServer.current || !addressedFetched || !previousFetched) return;
    seededFromServer.current = true;
    const emailsOf = (items?: MembershipEntity[]): string[] =>
      (items ?? []).map(client => client.user?.email).filter(Boolean) as string[];
    const addressedHere = emailsOf(addressedData?.items ?? sprintProductClients);
    const add = addressedHere.length ? addressedHere : emailsOf(previousData?.items);
    setRecipients(current => {
      const held = new Set(current.map(r => r.value.toLowerCase()));
      return [...current, ...toOptions(add.filter(email => !held.has(email.toLowerCase())))];
    });
  }, [addressedFetched, previousFetched, addressedData, previousData, sprintProductClients]);

  const selectedColleagues = recipients.filter(r => offeredEmails.has(r.value.toLowerCase())).map(r => r.value);

  const onColleaguesChange = (nextEmails: string[]) => {
    const externals = recipients.filter(r => !offeredEmails.has(r.value.toLowerCase()));
    const chosen = nextEmails.map(email => ({ value: email, label: email, isFixed: false }));
    setRecipients([...chosen, ...externals]);
  };

  const addressees = recipients.filter(r => !isCopied(r.value));
  // Copied-in people get the mail on every client's copy, so with no client there is nothing to copy them on.
  const noRecipients = addressees.length === 0;
  // Labelled at render: the managers arrive after the chips are seeded, so a chip built earlier kept a bare address.
  const recipientChips = recipients.map(r => ({
    ...r,
    label: isCopied(r.value) ? `${r.value} ${t('mailPreview.ccSuffix')}` : r.value,
  }));
  const [pickingRecipients, setPickingRecipients] = useState(false);

  // For the greeting, from whoever we know by that address. An unknown address is greeted as the API finds it.
  const firstNames = useMemo(() => {
    const known = new Map<string, string>();
    (sprintProductClients ?? []).forEach(client => {
      if (client.user?.email && client.user.firstName)
        known.set(client.user.email.toLowerCase(), client.user.firstName);
    });
    colleagueOptions.forEach(option => option.firstName && known.set(option.email.toLowerCase(), option.firstName));
    return known;
  }, [sprintProductClients, colleagueOptions]);
  // One mail per address, each opening with its own name: more than one makes the greeting a merge field.
  const greetedNames = useMemo(
    () =>
      [...new Set(addressees.map(r => firstNames.get(r.value.toLowerCase())))].filter((name): name is string => !!name),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recipients, firstNames, managerEmails],
  );

  const { settled: costSettled, totalCost, gaps, sprintStart, recheck } = useSprintReportCost(sprintProduct);
  // The team and its rates over the mail, as the incident step lays its entries over the report.
  const [pricing, setPricing] = useState(false);
  const [leavingPricing, setLeavingPricing] = useState(false);
  const leaveTimer = useRef<number>();
  useEffect(() => () => window.clearTimeout(leaveTimer.current), []);
  const closePricing = (): void => {
    recheck();
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setPricing(false);
      return;
    }
    setLeavingPricing(true);
    leaveTimer.current = window.setTimeout(() => {
      setLeavingPricing(false);
      setPricing(false);
    }, PRICING_LEAVE_MS);
  };
  // Priced off everyone's level and rate, so with one missing the mail goes without its cost.
  const cannotPrice = gaps.unlevelled.length + gaps.unrated.length + gaps.uncosted.length > 0;
  // Asked for once the cost is known, so the mail opens with its cost row rather than gaining it later.
  const editedRef = useRef<MailBlock[] | null>(null);
  const { data: draft } = useSprintReportMailDraft(
    sprintProduct?.id,
    greetedNames[0],
    totalCost,
    costSettled,
    () => editedRef.current,
  );
  const drafting = !draft;
  const [subject, setSubject] = useState('');
  /** What the sender changed in the body; null while it is still the draft, which the API composes itself. */
  const [blocks, setBlocks] = useState<MailBlock[] | null>(null);
  // Seeded once, so a refetch never undoes what the sender has already written.
  const seeded = useRef(false);
  useEffect(() => {
    if (!draft || seeded.current) return;
    seeded.current = true;
    setSubject(draft.subject);
  }, [draft]);
  editedRef.current = blocks;
  // A reprice of an edited body comes back as that body with the new cost row, which is now the edit.
  useEffect(() => {
    if (draft && editedRef.current) setBlocks(draft.blocks);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.html]);

  /*
   * Who to offer the client to once the send lands, and which client that is: the one this product is
   * done for. Somebody already standing at the client has nothing to gain from the prompt, and one of our
   * own can never be put on a client — so both are left out, and with nobody left it does not open.
   */
  const { data: companies } = useListClientCompanies(organizationId);
  const client = companies?.items?.find(company => company.products?.some(product => product.id === productId));
  const { data: clientDetail } = useClientCompany(organizationId, client?.id);
  const alreadyAtClient = useMemo(
    () => new Set((clientDetail?.people ?? []).map(person => person.email?.toLowerCase()).filter(Boolean)),
    [clientDetail?.people],
  );
  const [toOffer, setToOffer] = useState<Recipient[] | null>(null);

  const onSend = () => {
    // Flush a valid email still typed in the "To" box; null means it's invalid, so block sending.
    const flushed = recipientsFieldRef.current ? recipientsFieldRef.current.commitDraft() : recipients;
    if (flushed === null) return;
    const chosen = Array.from(new Set(flushed.map(x => x.value)));
    const copySender = chosen.some(isMe);
    const everyone = chosen.filter(email => !isMe(email));
    saveLastSent(productId, everyone);
    const emails = everyone.filter(email => !isCopied(email));
    const cc = everyone.filter(email => isCopied(email));

    const offerable = emails.filter(email => !alreadyAtClient.has(email.toLowerCase()));
    const willOffer = Boolean(client && productId && offerable.length);

    // The report goes either way. The prompt is a second question about a send that has landed, so it
    // waits for the send rather than asking about one that may have failed.
    // Only an edit is sent: the untouched draft is the API's own, and it composes that itself.
    const wording = {
      subject: draft && subject !== draft.subject ? subject : undefined,
      blocks: blocks ?? undefined,
      totalCost,
    };
    sendSprint(
      { emails, cc, copySender, sprintProductId: sprintProduct?.id, ...wording },
      { onSuccess: () => (willOffer ? setToOffer(recipientsFrom(offerable, [productId as string])) : undefined) },
    );
    if (!willOffer) handleClick(false);
  };

  if (toOffer && client) {
    return (
      <AddRecipientsToClientDialog
        organizationId={organizationId}
        clientCompanyId={client.id}
        clientName={client.name}
        products={(client.products ?? []).map(product => ({ id: product.id, name: product.name }))}
        recipients={toOffer}
        onDone={() => handleClick(false)}
      />
    );
  }

  return (
    <Dialog open onOpenChange={open => !open && handleClick(false)}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{t('titles.sendReport')}</DialogTitle>
        </DialogHeader>

        {/* Capped by the window, or a short screen pushes the dialog off its top. */}
        <div
          className={cx(
            'relative flex flex-col transition-[height] duration-300',
            pricing && !leavingPricing ? 'h-[min(720px,calc(100vh-176px))]' : 'h-[min(588px,calc(100vh-176px))]',
          )}
        >
          {/* Opaque and from below, over the mail rather than beside it: the mail stays mounted, edits and all. */}
          {pricing && productId && (
            <div
              className={cx(
                'absolute inset-0 z-10 bg-popover px-0.5 duration-300 ease-out motion-reduce:animate-none',
                leavingPricing
                  ? 'pointer-events-none animate-out fade-out-0 slide-out-to-bottom-4 fill-mode-forwards'
                  : 'animate-in fade-in-0 slide-in-from-bottom-4',
              )}
            >
              <SprintPricingPanel
                organizationId={organizationId}
                productId={productId}
                sprintStart={sprintStart}
                gaps={gaps}
                onBack={closePricing}
              />
            </div>
          )}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-border bg-card">
            <div className="flex shrink-0 items-start gap-2 border-b border-border px-4 py-2 text-sm">
              <div className="min-w-0 flex-1">
                <ApprovalRecipientsField
                  ref={recipientsFieldRef}
                  label={<span className="sr-only">{t('to')}</span>}
                  placeholder={t('mailPreview.recipients')}
                  invalidEmailMessage={t('invalidEmailDraft')}
                  value={recipientChips}
                  onChange={next => setRecipients(next.map(r => ({ ...r, label: r.value })))}
                  onBlur={() => undefined}
                  bare
                />
              </div>
              {offeredOptions.length > 0 && (
                <MultiSelectField
                  label={<span className="sr-only">{t('mailPreview.recipients')}</span>}
                  // A click inside the frame is invisible out here, so the frame tells the list to close.
                  open={pickingRecipients}
                  onOpenChange={setPickingRecipients}
                  options={offeredOptions.map(o => ({
                    value: o.email,
                    label: o.name || o.email,
                    description: o.name ? o.email : undefined,
                    tags: isCopied(o.email) ? [{ text: t('mailPreview.ccTag') }] : undefined,
                  }))}
                  value={selectedColleagues}
                  onChange={onColleaguesChange}
                  emptyLabel={t('mailPreview.selectRecipients')}
                  searchPlaceholder={t('searchColleagues')}
                  emptySearchLabel={t('approvalColleaguesNone')}
                  selectAllLabel={t('selectAll')}
                  summaryThreshold={1}
                  renderCountSummary={count => t('mailPreview.peopleSelected', { count })}
                  searchable={offeredOptions.length > 6}
                  className="w-40 shrink-0 gap-0"
                  contentClassName="w-80"
                  align="end"
                />
              )}
            </div>

            {cannotPrice && (
              <div
                role="alert"
                className="shrink-0 border-b border-border bg-amber-500/10 py-1.5 pl-[26px] pr-4 text-sm text-foreground"
              >
                {gaps.unlevelled.length > 0 && (
                  <p>
                    {t('mailPreview.seniorityMissing', {
                      count: gaps.unlevelled.length,
                      names: gaps.unlevelled.map(worker => worker.name).join(', '),
                    })}
                  </p>
                )}
                {gaps.unrated.length > 0 && (
                  <p>
                    {t('mailPreview.rateMissing', {
                      count: gaps.unrated.length,
                      names: gaps.unrated.map(worker => worker.name).join(', '),
                    })}
                  </p>
                )}
                {gaps.uncosted.length > 0 && (
                  <p>
                    {t('mailPreview.hourlyCostMissing', {
                      count: gaps.uncosted.length,
                      names: gaps.uncosted.map(worker => worker.name).join(', '),
                    })}
                  </p>
                )}
                {/* The panel fills in levels and rates; an hourly cost is set on the member's own page. */}
                {gaps.unlevelled.length + gaps.unrated.length > 0 && (
                  <p>
                    {t('mailPreview.fillBeforeSending')}{' '}
                    <button
                      type="button"
                      onClick={() => setPricing(true)}
                      className="font-medium underline underline-offset-2"
                    >
                      {t('mailPreview.fillInPricing')}
                    </button>
                  </p>
                )}
              </div>
            )}

            <div className="flex shrink-0 items-baseline border-b border-border px-4 py-1.5 text-sm">
              <Label htmlFor="sprint-report-mail-subject" className="sr-only">
                {t('mailPreview.subject')}
              </Label>
              {drafting ? (
                <Skeleton className="my-1 h-6 flex-1" />
              ) : (
                <Input
                  id="sprint-report-mail-subject"
                  value={subject}
                  onChange={event => setSubject(event.target.value)}
                  placeholder={t('mailPreview.subject')}
                  className="h-7 rounded-none border-0 bg-transparent px-2.5 font-medium shadow-none focus-visible:ring-0 dark:bg-transparent"
                />
              )}
            </div>

            <div className="flex min-h-0 flex-1 flex-col">
              {draft ? (
                <MailPreview
                  html={draft.html}
                  message=""
                  draftMessage=""
                  names={greetedNames}
                  onChange={(_text, slot) => setBlocks(readMailBlocks(slot))}
                  onPress={() => setPickingRecipients(false)}
                />
              ) : (
                <Skeleton className="min-h-0 w-full flex-1 rounded-none" />
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleClick(false)}>
            {t('actions.cancel')}
          </Button>
          <Button type="button" disabled={noRecipients} onClick={onSend}>
            {cannotPrice ? t('mailPreview.sendWithoutCosts') : t('actions.sendAndEmail')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendSprintPopup;
